import { useState, useEffect, useCallback, useRef } from 'react';
import { Users, Plus, X, Crown, UserMinus, ArrowRightLeft, Search, Check, Loader as Loader2, UserPlus, CircleAlert as AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { logAccountActivity } from '../../lib/activityLog';
import { useApi } from '../../lib/useApi';
import type { Account, AccountTeamMember } from '../../types';

interface OrgUser {
  user_id: string;
  email: string;
  full_name: string;
  role: string;
}

interface JoinRequest {
  id: string;
  requester_user_id: string;
  requester_name: string;
  requester_email: string;
  created_at: string;
}

interface Props {
  account: Account;
  isOwner: boolean;
  onClose: () => void;
}

export default function AccountTeamPanel({ account, isOwner, onClose }: Props) {
  const { user, organisation } = useAuth();
  const { apiGet, apiPost, apiPut, apiDelete } = useApi();
  const [members, setMembers] = useState<AccountTeamMember[]>([]);
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [adding, setAdding] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [transferTarget, setTransferTarget] = useState<string | null>(null);
  const [showTransferConfirm, setShowTransferConfirm] = useState(false);
  const [joinRequests, setJoinRequests] = useState<JoinRequest[]>([]);
  const [processingRequest, setProcessingRequest] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);

  const loadTeamData = useCallback(async () => {
    if (!organisation) return;
    setLoading(true);
    const data = await apiGet<{
      members: AccountTeamMember[];
      org_users: OrgUser[];
      join_requests: JoinRequest[];
    }>(`/v1/account/${account.id}/team?org_id=${encodeURIComponent(organisation.id)}`);
    setMembers(data.members || []);
    setOrgUsers(data.org_users || []);
    setJoinRequests(data.join_requests || []);
    setLoading(false);
  }, [account.id, apiGet, organisation]);

  useEffect(() => {
    loadTeamData().catch((err: unknown) => {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to load account team');
      setLoading(false);
    });
  }, [loadTeamData]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const memberUserIds = new Set(members.map(m => m.user_id));
  const availableUsers = orgUsers
    .filter(u => !memberUserIds.has(u.user_id))
    .filter(u => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return u.email.toLowerCase().includes(q) || u.full_name.toLowerCase().includes(q);
    });

  const addMember = async (targetUserId: string) => {
    if (!user || !organisation) return;
    setAdding(targetUserId);
    setErrorMsg('');

    try {
      await apiPost(`/v1/account/${account.id}/team`, {
        org_id: organisation.id,
        user_id: targetUserId,
      });
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to add team member. Please try again.');
      setAdding(null);
      return;
    }

    const target = orgUsers.find(u => u.user_id === targetUserId);
    const targetName = target?.full_name || target?.email || 'a team member';

    logAccountActivity(account.id, 'team_member_added', `Added ${targetName} to the account team`);
    await loadTeamData();
    setShowAdd(false);
    setSearchQuery('');
    setAdding(null);
  };

  const removeMember = async (memberId: string, _memberUserId: string) => {
    if (!user || !organisation) return;
    setRemoving(memberId);
    setErrorMsg('');

    const target = members.find(m => m.id === memberId);
    const targetName = target?.user_name || target?.user_email || 'a team member';

    try {
      await apiDelete(`/v1/account/${account.id}/team/${memberId}?org_id=${encodeURIComponent(organisation.id)}`);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to remove team member.');
      setRemoving(null);
      return;
    }

    logAccountActivity(account.id, 'team_member_removed', `Removed ${targetName} from the account team`);
    await loadTeamData();
    setRemoving(null);
  };

  const transferOwnership = async () => {
    if (!transferTarget || !user || !organisation) return;
    setAdding(transferTarget);

    const target = members.find(m => m.user_id === transferTarget);
    const targetName = target?.user_name || target?.user_email || 'a team member';

    await apiPost(`/v1/account/${account.id}/team/transfer`, {
      org_id: organisation.id,
      new_owner_user_id: transferTarget,
    });

    logAccountActivity(account.id, 'ownership_transferred', `Transferred ownership to ${targetName}`);

    setShowTransferConfirm(false);
    setTransferTarget(null);
    setAdding(null);
    await loadTeamData();
  };

  const approveRequest = async (request: JoinRequest) => {
    if (!user || !organisation) return;
    setProcessingRequest(request.id);
    setErrorMsg('');

    try {
      await apiPut(`/v1/account/${account.id}/team-requests/${request.id}`, {
        org_id: organisation.id,
        action: 'approve',
      });
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to approve request.');
      setProcessingRequest(null);
      return;
    }

    const requesterName = request.requester_name || request.requester_email || 'a colleague';
    logAccountActivity(account.id, 'team_member_added', `Approved join request from ${requesterName}`);

    setProcessingRequest(null);
    await loadTeamData();
  };

  const rejectRequest = async (request: JoinRequest) => {
    if (!user || !organisation) return;
    setProcessingRequest(request.id);

    await apiPut(`/v1/account/${account.id}/team-requests/${request.id}`, {
      org_id: organisation.id,
      action: 'reject',
    });

    setProcessingRequest(null);
    await loadTeamData();
  };

  const owner = members.find(m => m.role === 'owner');
  const nonOwners = members.filter(m => m.role !== 'owner');

  return (
    <div
      ref={panelRef}
      className="fixed inset-x-4 top-20 z-50 max-h-[calc(100vh-6rem)] overflow-hidden rounded-lg border border-app-line bg-app-surface sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96"
    >
      <div className="p-4 border-b border-app-line flex items-center justify-between">
        <h3 className="text-sm font-semibold text-app-ink flex items-center gap-2">
          <Users className="w-4 h-4 text-forest-600" />
          Account Team ({members.length})
        </h3>
        <button onClick={onClose} className="p-1 text-app-muted hover:text-app-muted rounded-lg hover:bg-app-canvas transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      {errorMsg && (
        <div className="mx-3 mt-3 flex items-center gap-2 p-2.5 bg-app-critical/10 border border-app-critical rounded-lg">
          <AlertCircle className="w-3.5 h-3.5 text-app-critical flex-shrink-0" />
          <p className="text-xs text-app-critical">{errorMsg}</p>
          <button onClick={() => setErrorMsg('')} className="ml-auto p-0.5 text-app-critical hover:text-app-critical"><X className="w-3 h-3" /></button>
        </div>
      )}
      <div className="max-h-[400px] overflow-y-auto">
        {loading ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="w-5 h-5 text-app-muted animate-spin" />
          </div>
        ) : (
          <div className="p-3 space-y-1">
            {owner && (
              <MemberRow
                member={owner}
                isCurrentUser={owner.user_id === user?.id}
                canManage={false}
                canTransfer={false}
                onRemove={() => {}}
                onTransfer={() => {}}
                removing={false}
              />
            )}
            {nonOwners.map(m => (
              <MemberRow
                key={m.id}
                member={m}
                isCurrentUser={m.user_id === user?.id}
                canManage={isOwner}
                canTransfer={isOwner && m.user_id !== user?.id}
                onRemove={() => removeMember(m.id, m.user_id)}
                onTransfer={() => { setTransferTarget(m.user_id); setShowTransferConfirm(true); }}
                removing={removing === m.id}
              />
            ))}
          </div>
        )}
      </div>

      {isOwner && joinRequests.length > 0 && (
        <div className="border-t border-app-line">
          <div className="px-4 py-2.5 flex items-center gap-2">
            <UserPlus className="w-3.5 h-3.5 text-app-warning" />
            <p className="text-xs font-semibold text-app-ink">
              Pending Requests ({joinRequests.length})
            </p>
          </div>
          <div className="px-3 pb-3 space-y-1">
            {joinRequests.map(req => (
              <div key={req.id} className="flex items-center justify-between p-2.5 rounded-lg bg-app-warning/10 border border-app-warning">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-app-warning/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-medium text-app-warning">
                      {(req.requester_name || req.requester_email).charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-app-ink truncate">{req.requester_name || req.requester_email}</p>
                    {req.requester_name && <p className="text-[10px] text-app-muted truncate">{req.requester_email}</p>}
                  </div>
                </div>
                {processingRequest === req.id ? (
                  <Loader2 className="w-4 h-4 text-app-muted animate-spin flex-shrink-0" />
                ) : (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => approveRequest(req)}
                      className="p-1.5 text-app-positive hover:bg-app-positive/10 rounded-lg transition-colors"
                      title="Approve"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => rejectRequest(req)}
                      className="p-1.5 text-app-critical hover:bg-app-critical/10 rounded-lg transition-colors"
                      title="Decline"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {isOwner && !showAdd && (
        <div className="p-3 border-t border-app-line">
          <button
            onClick={() => setShowAdd(true)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-forest-700 bg-forest-50 hover:bg-forest-100 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Team Member
          </button>
        </div>
      )}

      {isOwner && showAdd && (
        <div className="border-t border-app-line">
          <div className="p-3">
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search org members..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-app-line rounded-lg focus:ring-2 focus:ring-forest-500 focus:border-forest-500 outline-none"
                autoFocus
              />
            </div>
            <div className="max-h-[200px] overflow-y-auto space-y-1">
              {availableUsers.length === 0 ? (
                <p className="text-xs text-app-muted text-center py-4">
                  {searchQuery ? 'No matching members found' : 'All org members are already on this team'}
                </p>
              ) : (
                availableUsers.map(u => (
                  <button
                    key={u.user_id}
                    onClick={() => addMember(u.user_id)}
                    disabled={adding === u.user_id}
                    className="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-app-canvas transition-colors text-left group"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-7 h-7 rounded-full bg-app-canvas flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-app-muted">
                          {(u.full_name || u.email).charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-app-ink truncate">{u.full_name || u.email}</p>
                        {u.full_name && <p className="text-xs text-app-muted truncate">{u.email}</p>}
                      </div>
                    </div>
                    {adding === u.user_id ? (
                      <Loader2 className="w-4 h-4 text-forest-500 animate-spin flex-shrink-0" />
                    ) : (
                      <Plus className="w-4 h-4 text-app-line-strong group-hover:text-forest-500 flex-shrink-0 transition-colors" />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
          <div className="p-3 pt-0">
            <button
              onClick={() => { setShowAdd(false); setSearchQuery(''); }}
              className="w-full text-xs text-app-muted hover:text-app-ink py-1.5"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showTransferConfirm && transferTarget && (
        <div className="absolute inset-0 bg-app-surface/95 backdrop-blur-sm flex items-center justify-center p-6 z-10">
          <div className="text-center">
            <ArrowRightLeft className="w-8 h-8 text-app-warning mx-auto mb-3" />
            <h4 className="text-sm font-semibold text-app-ink mb-2">Transfer Ownership?</h4>
            <p className="text-xs text-app-muted mb-4 leading-relaxed">
              This will make {members.find(m => m.user_id === transferTarget)?.user_name || 'this person'} the owner.
              You will remain as a team member and can still view data and manage contacts.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => { setShowTransferConfirm(false); setTransferTarget(null); }}
                className="flex-1 px-3 py-2 text-sm text-app-muted border border-app-line rounded-lg hover:bg-app-canvas transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={transferOwnership}
                disabled={!!adding}
                className="flex-1 px-3 py-2 text-sm font-medium text-white bg-app-warning rounded-lg hover:bg-app-warning transition-colors"
              >
                {adding ? 'Transferring...' : 'Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MemberRow({ member, isCurrentUser, canManage, canTransfer, onRemove, onTransfer, removing }: {
  member: AccountTeamMember;
  isCurrentUser: boolean;
  canManage: boolean;
  canTransfer: boolean;
  onRemove: () => void;
  onTransfer: () => void;
  removing: boolean;
}) {
  const isOwner = member.role === 'owner';
  const displayName = member.user_name || member.user_email || 'Unknown';

  return (
    <div className="flex items-center justify-between p-2.5 rounded-lg hover:bg-app-canvas transition-colors group">
      <div className="flex items-center gap-2.5 min-w-0">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isOwner ? 'bg-app-warning/10' : 'bg-app-canvas'
        }`}>
          {isOwner ? (
            <Crown className="w-3.5 h-3.5 text-app-warning" />
          ) : (
            <span className="text-xs font-medium text-app-muted">
              {displayName.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-app-ink truncate">{displayName}</p>
            {isCurrentUser && <span className="text-[10px] text-app-muted">(you)</span>}
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
              isOwner
                ? 'bg-app-warning/10 text-app-warning'
                : 'bg-app-canvas text-app-muted'
            }`}>
              {isOwner ? 'Owner' : 'Member'}
            </span>
            {member.user_email && member.user_name && (
              <span className="text-[10px] text-app-muted truncate">{member.user_email}</span>
            )}
          </div>
        </div>
      </div>
      {!isOwner && (canManage || isCurrentUser) && (
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          {canTransfer && (
            <button
              onClick={onTransfer}
              className="p-1.5 text-app-muted hover:text-app-warning hover:bg-app-warning/10 rounded-lg transition-colors"
              title="Transfer ownership"
            >
              <ArrowRightLeft className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={onRemove}
            disabled={removing}
            className="p-1.5 text-app-muted hover:text-app-critical hover:bg-app-critical/10 rounded-lg transition-colors"
            title={isCurrentUser ? 'Leave team' : 'Remove from team'}
          >
            {removing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserMinus className="w-3.5 h-3.5" />}
          </button>
        </div>
      )}
    </div>
  );
}
